import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { 
  Megaphone, 
  AlertTriangle, 
  Info, 
  CheckCircle,
  Bell,
  Send,
  MessageSquare,
  Users,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const CommunicationPortal = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    type: 'info', // info, warning, success, error
    priority: 'normal', // low, normal, high, urgent
    targetAudience: 'all' // all, admins, staff, citizens
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/communications/announcements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      // Set mock data for demo
      setAnnouncements([
        {
          id: '1',
          title: 'System Maintenance Scheduled',
          message: 'The ReqTrak platform will undergo scheduled maintenance on January 15th from 2:00 AM to 4:00 AM EST. During this time, the system may be temporarily unavailable.',
          type: 'warning',
          priority: 'high',
          targetAudience: 'all',
          createdAt: '2025-01-10T10:00:00Z',
          createdBy: 'System Admin'
        },
        {
          id: '2',
          title: 'New AI Features Available',
          message: 'We\'ve launched AI-powered request classification and response generation features. These tools help streamline your workflow and improve response times.',
          type: 'success',
          priority: 'normal',
          targetAudience: 'admins',
          createdAt: '2025-01-08T14:30:00Z',
          createdBy: 'Product Team'
        },
        {
          id: '3',
          title: 'Security Update Deployed',
          message: 'A security update has been deployed to enhance platform protection. No action is required from your end.',
          type: 'info',
          priority: 'normal',
          targetAudience: 'all',
          createdAt: '2025-01-05T09:15:00Z',
          createdBy: 'Security Team'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/communications/announcements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAnnouncement)
      });

      if (response.ok) {
        toast.success('Announcement sent successfully!');
        setNewAnnouncement({
          title: '',
          message: '',
          type: 'info',
          priority: 'normal',
          targetAudience: 'all'
        });
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send announcement');
      }
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast.error('Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'warning': return 'border-amber-200 bg-amber-50';
      case 'success': return 'border-green-200 bg-green-50';
      case 'error': return 'border-red-200 bg-red-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'bg-gray-100 text-gray-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return <Badge className={variants[priority]}>{priority.toUpperCase()}</Badge>;
  };

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case 'admins': return <Users className="h-4 w-4" />;
      case 'staff': return <Users className="h-4 w-4" />;
      case 'citizens': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Communication Portal</h1>
          <p className="text-gray-600 mt-1">Send platform updates and system alerts</p>
        </div>
      </div>

      {/* Send New Announcement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Send Platform Update
          </CardTitle>
          <CardDescription>
            Broadcast important announcements to users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <Input
              value={newAnnouncement.title}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
              placeholder="Enter announcement title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Message</label>
            <Textarea
              value={newAnnouncement.message}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
              placeholder="Enter your message..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                value={newAnnouncement.type}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, type: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="info">Information</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                value={newAnnouncement.priority}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, priority: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Audience</label>
              <select
                value={newAnnouncement.targetAudience}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, targetAudience: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="all">All Users</option>
                <option value="admins">Department Admins</option>
                <option value="staff">Staff Members</option>
                <option value="citizens">Citizens</option>
              </select>
            </div>
          </div>

          <Button 
            onClick={sendAnnouncement}
            disabled={sending}
            className="w-full"
          >
            {sending ? (
              <>
                <Send className="h-4 w-4 mr-2 animate-pulse" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Announcement
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Recent Announcements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Recent Platform Updates
          </CardTitle>
          <CardDescription>
            View previously sent announcements and system alerts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <div key={announcement.id} className={`border rounded-lg p-4 ${getTypeColor(announcement.type)}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(announcement.type)}
                    <h3 className="font-semibold">{announcement.title}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPriorityBadge(announcement.priority)}
                    <Badge variant="outline" className="flex items-center gap-1">
                      {getAudienceIcon(announcement.targetAudience)}
                      {announcement.targetAudience}
                    </Badge>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-3">{announcement.message}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(announcement.createdAt).toLocaleString()}
                  </span>
                  <span>by {announcement.createdBy}</span>
                </div>
              </div>
            ))}
          </div>

          {announcements.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No announcements sent yet.</p>
              <p className="text-sm">Use the form above to send your first platform update.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationPortal;